<template>
  <div
    id="sequence_overview"
    class="flex flex-col overflow-y-scroll custom_scrollbar pr-4"
    style="height: calc(100vh - (185px + var(--banner-height)))"
  >
    <div
      v-if="loading"
      class="flex flex-col items-center justify-center gap-2 mt-20"
    >
      <CustomLoader :size="28" />
    </div>
    <div v-else>
      <div v-if="steps.length > 0" class="flex flex-col items-center gap-4">
        <SequenceStep
          :can-edit="canEdit"
          v-for="step in steps"
          :key="step.id"
          :step="step"
          :total-steps="steps.length"
          :sequences="sequences"
          :filters="filters"
          @update:sequence_step="fetchSequenceSteps"
          @add-step-modal="openAddStepModal"
        />
        <a-button
          v-if="canEdit"
          class="primary_medium mt-1 w-fit"
          @click="addStepAtEnd"
        >
          + Add a new step
        </a-button>
      </div>

      <div v-else class="flex flex-col items-center justify-center gap-2 mt-20">
        <ThemeImage
          :icon="imageNames.EMPTY_STATE_SECONDARY"
          alt="empty_icon"
          class="size-20"
        />
        <span class="text-base text_color font-semibold"
          >Add steps to get started</span
        >
        <span class="text-base secondary_color text-center">
          Set up a sequence of messages and tasks to keep your contacts<br />
          and leads engaged across multiple channels
        </span>
        <a-button class="primary_medium" @click="addStepAtEnd"
          >Add Step</a-button
        >
      </div>
    </div>

    <StepCreationModal
      v-model:visible="addStepModal"
      :sequence-id="sequenceId"
      :steps-length="steps.length"
      :step-status="stepStatus"
      :target-position="targetPosition"
      @step-created="fetchSequenceSteps"
    />
  </div>
</template>

<script setup>
import { onMounted, ref } from "vue";
import { useStore } from "vuex";
import CustomLoader from "@/src/components/CustomLoader.vue";
import SequenceService from "@/src/api/SequenceService.js";
import SequenceStep from "@/src/views/sequence/step/SequenceStep.vue";
import StepCreationModal from "./StepCreationModal.vue";
import OutreachService from "@/src/api/OutreachService.js";
import { getFeatureBlockStatus } from "@/src/helpers/stepOptionsHelper.js";
import { imageNames } from "@/src/constants/imageConstants";

const store = useStore();

const emit = defineEmits(["steps-changed"]);

const props = defineProps({
  sequenceId: { type: [String, Number], required: false },
});

const canEdit = ref(false);
const loading = ref(true);
const addStepModal = ref(false);
const steps = ref([]);
const sequences = ref([]);
const filters = ref([]);
const targetPosition = ref(null);

const stepStatus = ref({
  ai_email_agent: { active: true, disabled: false },
  ai_voice_agent: { active: true, disabled: false },
  qualification_agent: { active: true, disabled: false },
  market_research_agent: { active: true, disabled: false },
  multi_threading_agent: { active: true, disabled: false },
  next_best_action: { active: true, disabled: false },
  ai_linkedin_message_agent: { active: true, disabled: false },
  ai_linkedin_inmail_agent: { active: true, disabled: false },
  automatic_email: { active: true, disabled: false },
  linkedin_message: { active: true, disabled: false },
  linkedin_inmail: { active: true, disabled: false },
  linkedin_connection_request: { active: true, disabled: false },
  whatsapp_message: { active: false, disabled: false },
  task: { active: false, disabled: false },
  manual_email: { active: true, disabled: false },
  phone_call: { active: true, disabled: false },
  linkedin_message_task: { active: true, disabled: false },
  linkedin_inmail_task: { active: true, disabled: false },
  linkedin_connection_request_task: { active: true, disabled: false },
  whatsapp_message_task: { active: false, disabled: false },
});

const openAddStepModal = (position) => {
  targetPosition.value = position;
  addStepModal.value = true;
};

const addStepAtEnd = () => {
  targetPosition.value = steps.value.length + 1;
  addStepModal.value = true;
};

const fetchSequenceSteps = async () => {
  loading.value = true;
  try {
    const response = await SequenceService.fetchSequenceSteps(props.sequenceId);
    if (response?.success) {
      steps.value = response?.steps || [];
      canEdit.value = response?.can_edit || false;
      emit("steps-changed");
    }
  } catch (error) {
    console.error(`Failed to fetch Sequence steps..`, error);
  } finally {
    loading.value = false;
  }
};

const getCampaignAllFilters = async () => {
  try {
    const response = await OutreachService.campaignAllFilters();
    if (response?.success) {
      filters.value = response?.filters || [];
    }
  } catch (error) {
    console.error("An error occurred while loading filters: ", error);
  }
};

const fetchValidSequences = async () => {
  try {
    const response = await SequenceService.fetchValidSequences();
    if (response?.success) {
      sequences.value = response?.sequences || [];
    }
  } catch (error) {
    console.error("An error occurred while fetching sequences: ", error);
  }
};

onMounted(async () => {
  const featureBlockStatus = await getFeatureBlockStatus(store);
  Object.keys(featureBlockStatus).forEach((key) => {
    if (stepStatus.value[key]) {
      stepStatus.value[key] = featureBlockStatus[key];
    }
  });

  getCampaignAllFilters();
  fetchValidSequences();
  fetchSequenceSteps();
});
</script>
